import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { new_password_path, session_path, signup_path } from '@/rails/routes';
import { Form, Link, usePage } from '@inertiajs/react';

const TEXT_LINK =
  'rounded-sm underline decoration-accent underline-offset-4 hover:decoration-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

interface Props {
  emailAddress?: string;
  error?: string | null;
}

const New = ({ emailAddress, error }: Props) => {
  const { errors } = usePage<{ errors?: Record<string, string | string[]> }>().props;

  const errorFor = (key: string): string | undefined => {
    const value = errors?.[key];

    return Array.isArray(value) ? value[0] : value;
  };

  // A flash message is the layout's to show. This page shows only its own error.
  const generalError = error || errorFor('base') || errorFor('session');
  const emailError = errorFor('email_address') || errorFor('emailAddress');

  return (
    <>
      <h1 className="authority-display text-3xl leading-tight">Log in</h1>

      <Form method="post" action={session_path()} className="mt-6 flex flex-col gap-5">
        {({ processing, errors: formErrors }) => {
          const passwordError = formErrors.password || errorFor('password');

          return (
            <>
              {generalError ? (
                <p
                  role="alert"
                  className="border-destructive text-destructive border-l-2 pl-3 text-sm"
                >
                  {generalError}
                </p>
              ) : null}

              <div className="flex flex-col gap-2">
                <Label htmlFor="email_address">Email</Label>
                <Input
                  id="email_address"
                  type="email"
                  name="email_address"
                  autoComplete="email"
                  autoFocus
                  required
                  defaultValue={emailAddress ?? ''}
                  aria-invalid={emailError ? true : undefined}
                  aria-describedby={emailError ? 'email_address_error' : undefined}
                />
                {emailError ? (
                  <p id="email_address_error" className="text-destructive text-sm">
                    {emailError}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between gap-4">
                  <Label htmlFor="password">Password</Label>
                  <Link href={new_password_path()} className={`text-sm ${TEXT_LINK}`}>
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  aria-invalid={passwordError ? true : undefined}
                  aria-describedby={passwordError ? 'password_error' : undefined}
                />
                {passwordError ? (
                  <p id="password_error" className="text-destructive text-sm">
                    {passwordError}
                  </p>
                ) : null}
              </div>

              <Button type="submit" size="lg" disabled={processing}>
                Log in
              </Button>
            </>
          );
        }}
      </Form>

      <p className="text-muted-foreground mt-6 text-sm">
        No account yet?{' '}
        <Link href={signup_path()} className={`text-foreground ${TEXT_LINK}`}>
          Create an account
        </Link>
      </p>
    </>
  );
};

export default New;
